import { productsReferentialApiConnector } from 'apiConnector/products-referential';
import type { RecentProductWorkspaceType } from 'epic/common-form/fetchProductsReferential';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { SelectedProductVariation } from 'models/productsReferential';
import { ofType } from 'redux-observable';
import { merge, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import type { EpicWithThunk } from 'state/state.common';
import type { WorkspaceType } from 'state/workspaces/model';
import {
  selectedWorkspaceProductSavedToRecentProducts,
  workspaceTabProductSelectionSubmitFailed,
} from 'state/workspaces/workspace.actions';
import { isWorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';
import { httpClient } from 'utils/http/httpClient';

type RecentProductEvent = {
  recentWorkspaceType: RecentProductWorkspaceType;
  workspaceType: WorkspaceType;
  tabId: string;
  productVariation: SelectedProductVariation;
  client: Nullable<ISalesCounterpart>;
};

export function addOpenedProductToRecentProductsReferentialEpic(): EpicWithThunk {
  return (action$, _state$) => {
    const fromSelection$ = action$.pipe(
      ofType('WORKSPACE_TAB_PRODUCT_SELECTION_SUBMITTED'),
      filter(({ workspaceType }) => isWorkspaceWithProductSelector(workspaceType)),
      map((action): RecentProductEvent => {
        let recentWorkspaceType: RecentProductWorkspaceType;
        switch (action.workspaceType) {
          case 'dynamic':
          case 'static':
          case 'essential':
            recentWorkspaceType = action.workspaceType;
            break;
          default:
            recentWorkspaceType = 'batch';
        }
        return {
          recentWorkspaceType,
          workspaceType: action.workspaceType,
          tabId: action.tabId,
          productVariation: action.productVariation,
          client: action.client,
        };
      }),
    );

    const fromImport$ = action$.pipe(
      ofType('WORKSPACE_QUOTE_RELOADED'),
      filter(action => action.workspace === 'essential' && action.productVariation !== undefined),
      map((action): RecentProductEvent => ({
        recentWorkspaceType: 'essential',
        workspaceType: 'essential',
        tabId: action.temporaryFormId,
        productVariation: {
          productType: action.productVariation!.productType,
          productSubType: action.productVariation!.productSubtype,
          wrapper: action.productVariation!.wrapper,
        } as SelectedProductVariation,
        client: null,
      })),
    );

    return merge(fromSelection$, fromImport$).pipe(
      switchMap(({ recentWorkspaceType, workspaceType, tabId, productVariation, client }) =>
        productsReferentialApiConnector(httpClient)
          .addToRecentProduct(recentWorkspaceType, productVariation)
          .pipe(
            map(() => selectedWorkspaceProductSavedToRecentProducts(tabId, productVariation, client, workspaceType)),
            catchError(() => of(workspaceTabProductSelectionSubmitFailed(tabId))),
          ),
      ),
    );
  };
}
